import {useCallback, useMemo, useRef, useState} from 'preact/hooks';

import {useMonorail} from '~/foundation/Monorail/hooks';
import type {
  AnalyticsData,
  TrackPageImpressionParams,
} from '~/foundation/Monorail/types';
import {getTrekkieAttributes} from '~/foundation/Monorail/utils';
import type {SdkErrorCode} from '~/types/authorize';
import {isoNavigator} from '~/utils/navigator';
import {isoWindow} from '~/utils/window';

import type {LeadCaptureProps} from './types';

interface TrackFirstTimeRenderParams {
  eventName?: string;
  startTime?: number;
}

export function getAnalyticsProps({
  apiKey,
}: LeadCaptureProps): Partial<AnalyticsData> {
  return {
    apiKey,
    flow: 'discount',
    // eslint-disable-next-line no-warning-comments
    // TODO: Update for phone capture, check with data for shown/saved configs
    // https://github.com/Shopify/shop-identity/issues/3085
    flowVersion: 'EMAIL_CAPTURE',
  };
}

export function useLeadCaptureMonorail() {
  const {analyticsData, produceMonorailEvent, trackPageImpression} =
    useMonorail();
  const [trackedFirstTimeRenders] = useState<Set<string>>(new Set<string>());
  const [initTimestamp] = useState<number>(new Date().getTime());
  const shopAccountUuidRef = useRef<string>();

  const setShopAccountUuid = useCallback((shopAccountUuid: string) => {
    shopAccountUuidRef.current = shopAccountUuid;
  }, []);

  const trackError = useCallback(
    (code: SdkErrorCode, message: string) => {
      const {
        analyticsTraceId,
        apiKey,
        flow,
        flowVersion,
        shopPermanentDomain = isoWindow.Shopify?.shop || '',
      } = analyticsData;

      if (!apiKey || !flow || !flowVersion) {
        return;
      }

      produceMonorailEvent({
        event: {
          schemaId: 'shopify_pay_login_with_shop_sdk_error_events/1.0',
          payload: {
            analyticsTraceId,
            apiKey,
            errorCode: code,
            errorMessage: message,
            flow,
            flowVersion,
            sdkVersion: '__buildVersionBeta',
            shopPermanentDomain,
          },
        },
      });
    },
    [analyticsData, produceMonorailEvent],
  );

  const trackDiscountStatus = useCallback(
    (discountCode: string) => {
      const {
        analyticsTraceId,
        apiKey,
        flow,
        flowVersion,
        shopPermanentDomain = isoWindow.Shopify?.shop || '',
      } = analyticsData;

      if (!apiKey || !flow || !flowVersion) {
        return;
      }

      produceMonorailEvent({
        event: {
          schemaId: 'shopify_pay_login_with_shop_sdk_discount_status/2.0',
          payload: {
            analyticsTraceId,
            apiKey,
            discountCode,
            discountCodeStatus: 'received',
            flow,
            flowVersion,
            shopPermanentDomain,
          },
        },
      });
    },
    [analyticsData, produceMonorailEvent],
  );

  const trackFirstTimeRender = useCallback(
    async ({
      eventName,
      startTime = initTimestamp,
    }: TrackFirstTimeRenderParams = {}) => {
      const {analyticsTraceId, flowVersion} = analyticsData;
      const trackedEventName = eventName ?? flowVersion;

      if (!trackedEventName || trackedFirstTimeRenders.has(trackedEventName)) {
        return;
      }

      trackedFirstTimeRenders.add(trackedEventName);
      const timestamp = new Date().getTime();
      const duration = timestamp - startTime;

      const trekkieAttributes = await getTrekkieAttributes('shopId');

      produceMonorailEvent({
        event: {
          schemaId: 'shop_js_ui_shop_login_first_time_render/1.0',
          payload: {
            ...trekkieAttributes,
            analyticsTraceId,
            duration,
            shopLoginVersion: trackedEventName,
            url: isoWindow.location.href,
            userAgent: isoNavigator.userAgent,
          },
        },
        onError: () => {
          trackedFirstTimeRenders.delete(trackedEventName);
        },
        trekkieAttributes,
      });
    },
    [
      analyticsData,
      initTimestamp,
      produceMonorailEvent,
      trackedFirstTimeRenders,
    ],
  );

  const trackLeadCapturePageImpression = useCallback(
    (params: TrackPageImpressionParams) => {
      trackPageImpression({
        shopAccountUuid: shopAccountUuidRef.current,
        ...params,
      });
    },
    [trackPageImpression],
  );

  return useMemo(
    () => ({
      setShopAccountUuid,
      trackDiscountStatus,
      trackError,
      trackFirstTimeRender,
      trackLeadCapturePageImpression,
    }),
    [
      setShopAccountUuid,
      trackDiscountStatus,
      trackError,
      trackFirstTimeRender,
      trackLeadCapturePageImpression,
    ],
  );
}
